import { print } from 'graphql';
import { gql } from 'graphql-tag';
import type { MigrationStrategies, RxCollection, RxDocument, RxJsonSchema } from 'rxdb';

import { defaultDoc } from '../share/defaultDoc';

export interface DeficiencyComments {
  readonly pkUuid: string;
  readonly pkDeficiencyWiseAuditComments: number;
  readonly fkAssetInspectionComponentFailure: number;
  readonly inspectorId: number;
  readonly inspectorRoleId: number | undefined;
  readonly inspectionTaskId: number;
  readonly taskStatus: number;
  readonly comment: string;
  readonly dateCreated?: string;
  readonly dateModified?: string;
  readonly createdBy?: string;
  readonly isActive?: boolean;
  readonly attentionFlag: boolean;
  readonly inspectorName?: string;
  readonly componentVersionInspectionId?: number;
  readonly componentVersionId?: number;
  readonly isVersionComment: boolean;
  readonly clerkName?: string;
}

export const deficiencyCommentsSchema: RxJsonSchema<DeficiencyComments> = {
  type: 'object',
  version: 3,
  description: 'describes deficiency comments',
  primaryKey: 'pkUuid',
  properties: {
    pkUuid: { type: 'string', maxLength: 36 },
    pkDeficiencyWiseAuditComments: { type: ['number', 'null'] },
    fkAssetInspectionComponentFailure: { type: ['number', 'null'] },
    inspectorId: { type: ['number', 'null'] },
    inspectorRoleId: { type: ['number', 'null'] },
    inspectionTaskId: { type: ['number', 'null'] },
    taskStatus: { type: ['number', 'null'] },
    comment: { type: ['string', 'null'] },
    dateCreated: { type: 'string', maxLength: 36 },
    dateModified: { type: ['string', 'null'] },
    createdBy: { type: ['string', 'null'] },
    isActive: { type: ['boolean', 'null'] },
    attentionFlag: { type: ['boolean', 'null'] },
    inspectorName: { type: ['string', 'null'] },
    clerkName: { type: ['string', 'null'] },
    componentVersionId: { type: ['number', 'null'] },
    componentVersionInspectionId: { type: ['number', 'null'] },
    isVersionComment: { type: ['boolean', 'null'] },
  },
  indexes: ['dateCreated'],
};

export type DeficiencyCommentsCollection = RxCollection<DeficiencyComments> | null;
export type DeficiencyCommentsDocument = RxDocument<DeficiencyComments>;

export const deficiencyCommentsPullQueryBuilder = (document: DeficiencyComments) => {
  let doc = document;
  if (!doc) {
    (doc as unknown) = { ...defaultDoc };
  }

  const query = print(gql`
    query mobileDeficiencyWiseAuditComments($lastRequested: DateTime!) {
      mobileDeficiencyWiseAuditComments(lastRequested: $lastRequested) {
        pkUuid
        pkDeficiencyWiseAuditComments
        fkAssetInspectionComponentFailure
        inspectorId
        inspectorRoleId
        comment
        dateCreated
        dateModified
        createdBy
        isActive
        attentionFlag
        inspectorName
        clerkName
        componentVersionInspectionId
        componentVersionId
      }
    }
  `);

  return {
    query,
    variables: {
      lastRequested: doc?.dateModified ? doc?.dateModified : doc?.dateCreated,
    },
  };
};

export const deficiencyCommentsMigrationStrategies: MigrationStrategies = {
  1: (oldDoc: DeficiencyCommentsDocument) => {
    return oldDoc;
  },
  2: (oldDoc: DeficiencyCommentsDocument) => {
    const newDoc = {
      ...oldDoc,
      inspectorName: oldDoc.inspectorName || null,
      componentVersionId: oldDoc.componentVersionId || null,
      componentVersionInspectionId: oldDoc.componentVersionInspectionId || null,
      isVersionComment: oldDoc.isVersionComment || null,
    };
    return newDoc;
  },
  3: (oldDoc: DeficiencyCommentsDocument) => {
    const newDoc = {
      ...oldDoc,
      inspectorName: oldDoc.clerkName || null,
    };
    return newDoc;
  },
};

export type DeficiencyVersionCommentsCollection = RxCollection<DeficiencyComments> | null;
export type DeficiencyVersionCommentsDocument = RxDocument<DeficiencyComments>;

export const deficiencyVersionCommentsPullQueryBuilder = (document: DeficiencyComments) => {
  let doc = document;
  if (!doc) {
    (doc as unknown) = { ...defaultDoc };
  }

  const query = print(gql`
    query mobileDeficiencyVersionAuditComments($lastRequested: DateTime!) {
      mobileDeficiencyVersionAuditComments(lastRequested: $lastRequested) {
        pkUuid
        pkDeficiencyWiseAuditComments
        fkAssetInspectionComponentFailure
        inspectorId
        inspectorRoleId
        comment
        dateCreated
        dateModified
        createdBy
        isActive
        attentionFlag
        inspectorName
        componentVersionInspectionId
        componentVersionId
        isVersionComment
      }
    }
  `);

  return {
    query,
    variables: {
      lastRequested: doc?.dateModified ? doc?.dateModified : doc?.dateCreated,
    },
  };
};

export default {};
